import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Taro from '@tarojs/taro';
import { Card } from '@/types/game';
import { BOARD_WIDTH, CARD_SIZE } from '@/data/board';
import { applyMove, GameState, getSlotCards } from '@/utils/gameEngine';
import { GameAction } from '@/pages/game/reducer';

interface Bounds { left: number; top: number; width: number; height: number }
export const MOTION_TIMING = { takeoff: 16, flight: 190, reflow: 110, frame: 16, cadence: 12, settle: 24, match: 130 };
export interface Flight {
  key: number; card: Card; from: Bounds; to: Bounds; root: Bounds; travelling: boolean; duration: number;
}
interface Arrival {
  key: number; cardId: string; ready: boolean; prepared: boolean; landed: boolean;
  inserted: Card[]; after: Card[]; matches: string[];
  launchAt: number; arrivalAt: number; finishAt: number;
}
interface Layout { board: Bounds; root: Bounds; targets: Bounds[]; staged: Record<string, Bounds> }

/** Immediate rule transactions, overlapping flights, reserved gaps before landing. */
export const useCardMotion = (
  state: GameState, dispatch: React.Dispatch<GameAction>, animations: boolean,
  onSound: (matched: boolean) => void
) => {
  const shadow = useRef(state), rendered = useRef(state);
  if (rendered.current !== state) {
    rendered.current = state;
    if (shadow.current.runId !== state.runId || state.revision >= shadow.current.revision) shadow.current = state;
  }
  const animationsRef = useRef(animations); animationsRef.current = animations;
  const soundRef = useRef(onSound); soundRef.current = onSound;
  const layout = useRef<Layout | null>(null), serial = useRef(0), generation = useRef(0);
  const arrivals = useRef<Arrival[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [queue, setQueue] = useState(() => getSlotCards(state));
  const [hiddenSlotIds, setHiddenSlotIds] = useState<string[]>([]);
  const [hiddenTransitionIds, setHiddenTransitionIds] = useState<string[]>([]);
  const previousState = useRef(state);
  const [eliminatingIds, setEliminatingIds] = useState<string[]>([]);
  const [hasPending, setHasPending] = useState(false);
  const later = useCallback((callback: () => void, delay: number) => {
    const version = generation.current;
    const timer = setTimeout(() => {
      timers.current = timers.current.filter(item => item !== timer);
      if (version === generation.current) callback();
    }, Math.max(0, delay));
    timers.current.push(timer);
  }, []);
  const clear = useCallback(() => {
    generation.current++; timers.current.forEach(clearTimeout); timers.current = []; arrivals.current = [];
  }, []);
  const flush = useCallback(() => {
    clear(); setFlights([]); setHiddenSlotIds([]); setHiddenTransitionIds([]); setEliminatingIds([]); setHasPending(false);
    setQueue(getSlotCards(shadow.current));
  }, [clear]);
  const pump = useCallback(function presentNext() {
    const first = arrivals.current[0];
    if (!first || first.landed) return;
    const segment: Arrival[] = [];
    for (const arrival of arrivals.current) {
      segment.push(arrival);
      if (arrival.matches.length) break;
    }
    if (segment.some(arrival => !arrival.prepared)) {
      segment.forEach(arrival => { arrival.prepared = true; });
      const planned = segment[segment.length - 1].inserted;
      // Reserve every in-flight card together, rather than waiting a full reflow per tap.
      setQueue(planned); setHiddenSlotIds(segment.filter(arrival => !arrival.landed).map(arrival => arrival.cardId));
      const geometry = layout.current, now = Date.now();
      if (geometry) setFlights(value => value.map(flight => {
        const arrival = segment.find(item => item.key === flight.key);
        const index = arrival ? planned.findIndex(card => card.id === arrival.cardId) : -1;
        const to = geometry.targets[index];
        if (!arrival || !to || (flight.to.left === to.left && flight.to.top === to.top)) return flight;
        // Retarget a flying tile using its remaining time, so it never jumps at landing.
        return { ...flight, to, duration: Math.max(MOTION_TIMING.frame, arrival.arrivalAt - (flight.travelling ? now : arrival.launchAt)) };
      }));
    }
    if (!first.ready) return;
    first.landed = true;
    setFlights(value => value.filter(item => item.key !== first.key));
    setHiddenSlotIds(value => value.filter(id => id !== first.cardId));
    const finish = () => {
      if (first.matches.length || arrivals.current.length === 1) setQueue(first.after);
      setEliminatingIds([]); arrivals.current.shift();
      if (!arrivals.current.length) setHasPending(false); else presentNext();
    };
    if (first.matches.length) {
      later(() => { setEliminatingIds(first.matches); soundRef.current(true); }, MOTION_TIMING.settle);
      later(finish, MOTION_TIMING.settle + MOTION_TIMING.match);
    } else finish();
  }, [later]);

  const refreshLayout = useCallback(() => {
    if (typeof Taro.createSelectorQuery !== 'function') return;
    const runId = shadow.current.runId;
    const query = Taro.createSelectorQuery().select('#board-canvas').boundingClientRect()
      .select('#game-playfield').boundingClientRect();
    for (let index = 0; index < 7; index++) query.select('#slot-target-' + index).boundingClientRect();
    const reserveIds = [...shadow.current.reserveCardIds];
    reserveIds.forEach(id => query.select('#tile-' + id).boundingClientRect());
    query.exec(results => {
      if (shadow.current.runId !== runId) return;
      const [board, root, ...rest] = results as (Bounds | null)[];
      if (!board?.width || !root?.width || rest.slice(0, 7).some(item => !item?.width)) return;
      const staged: Record<string, Bounds> = {};
      reserveIds.forEach((id, index) => { if (rest[index + 7]?.width) staged[id] = rest[index + 7]!; });
      layout.current = { board, root, targets: rest.slice(0, 7) as Bounds[], staged };
    });
  }, []);
  useLayoutEffect(() => {
    const before = previousState.current; previousState.current = state;
    if (!animationsRef.current || before.runId !== state.runId || state.phase !== 'playing' || !layout.current) return;
    if (state.history.length >= before.history.length && state.reserveCardIds.length <= before.reserveCardIds.length) return;
    const old = new Map(before.cards.map(card => [card.id, card]));
    const moved = state.cards.filter(card => (card.status === 0 || card.status === 3) && [1, 2].includes(old.get(card.id)?.status ?? -1));
    if (!moved.length || typeof Taro.createSelectorQuery !== 'function') return;
    const geometry = layout.current, token = generation.current;
    const moving = moved.map(card => {
      const same = getSlotCards(state).reduce((index, item, i) => item.type === card.type ? i : index, -1);
      const slot = before.slotCardIds.indexOf(card.id);
      const from = geometry.targets[Math.min(6, slot >= 0 ? slot : Math.max(0, same + 1))];
      const key = ++serial.current;
      return { key, card: { ...card, id: card.id + '-return-' + key, status: 0, isCover: false }, from,
        to: from, root: geometry.root, travelling: false, duration: MOTION_TIMING.flight };
    });
    const keys = new Set(moving.map(item => item.key)), ids = new Set(moved.map(card => card.id));
    setHiddenTransitionIds(value => [...value, ...ids]); setFlights(value => [...value, ...moving]);
    const finish = () => {
      setFlights(value => value.filter(item => !keys.has(item.key)));
      setHiddenTransitionIds(value => value.filter(id => !ids.has(id)));
    };
    const deadline = setTimeout(() => { if (generation.current === token) finish(); }, 500);
    timers.current.push(deadline);
    const queryDestinations = () => {
      if (generation.current !== token) return;
      const query = Taro.createSelectorQuery();
      moved.forEach(card => query.select('#tile-' + card.id).boundingClientRect());
      query.exec(results => {
        if (generation.current !== token) return;
        if (results.length !== moving.length || (results as (Bounds | null)[]).some(bounds => !bounds?.width)) { clearTimeout(deadline); finish(); return; }
        clearTimeout(deadline);
        later(() => setFlights(value => value.map(flight => {
          const index = moving.findIndex(item => item.key === flight.key);
          return index < 0 ? flight : { ...flight, to: results[index] as Bounds, travelling: true };
        })), MOTION_TIMING.takeoff);
        later(finish, MOTION_TIMING.takeoff + MOTION_TIMING.flight);
      });
    };
    if (typeof Taro.nextTick === 'function') Taro.nextTick(queryDestinations); else later(queryDestinations, 16);
  }, [state, later]);
  useEffect(() => {
    if (['ready', 'paused', 'quiz'].includes(state.phase)) flush();
    else if (!arrivals.current.length) setQueue(getSlotCards(state));
  }, [state, flush]);
  useEffect(() => { flush(); layout.current = null; later(refreshLayout, 50); }, [state.runId, flush, later, refreshLayout]);
  useEffect(() => () => clear(), [clear]);

  const click = useCallback((id: string) => {
    const current = shadow.current, now = Date.now();
    const next = applyMove(current, id, now);
    if (next === current) return;
    const card = current.cards.find(item => item.id === id)!;
    const before = getSlotCards(current);
    const last = before.reduce((index, item, i) => item.type === card.type ? i : index, -1);
    const targetIndex = last < 0 ? before.length : last + 1;
    const inserted = [...before]; inserted.splice(targetIndex, 0, { ...card, status: 1, isCover: false });
    const matches = inserted.filter(item => item.type === card.type), matched = matches.length >= 3;
    const geometry = layout.current, scale = geometry ? geometry.board.width / BOARD_WIDTH : 0;
    const from = card.status === 3 ? geometry?.staged[id] : geometry && {
      left: geometry.board.left + card.x * scale, top: geometry.board.top + card.y * scale,
      width: (card.size ?? CARD_SIZE) * scale, height: (card.size ?? CARD_SIZE) * scale
    };
    shadow.current = next; dispatch({ type: 'CLICK', cardId: id, runId: current.runId, now }); soundRef.current(false);
    if (!animationsRef.current || !from || !geometry?.targets[targetIndex]) {
      flush(); if (matched) soundRef.current(true); if (animationsRef.current) later(refreshLayout, 16); return;
    }
    const key = ++serial.current, previous = arrivals.current[arrivals.current.length - 1];
    const reflow = targetIndex < before.length ? MOTION_TIMING.reflow : MOTION_TIMING.frame;
    const arrivalAt = Math.max(now + MOTION_TIMING.takeoff + MOTION_TIMING.flight,
      previous ? previous.finishAt + (previous.matches.length ? reflow : MOTION_TIMING.cadence) : 0);
    arrivals.current.push({ key, cardId: id, ready: false, prepared: false, landed: false,
      inserted, after: getSlotCards(next), matches: matched ? matches.slice(0, 3).map(item => item.id) : [],
      launchAt: now + MOTION_TIMING.takeoff, arrivalAt,
      finishAt: arrivalAt + (matched ? MOTION_TIMING.settle + MOTION_TIMING.match : 0) });
    setHasPending(true);
    setFlights(value => [...value, { key, card: { ...card, id: card.id + '-flight-' + key, status: 0, isCover: false },
      from, to: geometry.targets[targetIndex], root: geometry.root, travelling: false,
      duration: arrivalAt - now - MOTION_TIMING.takeoff }]);
    pump();
    later(() => setFlights(value => value.map(item => item.key === key ? { ...item, travelling: true } : item)), MOTION_TIMING.takeoff);
    later(() => { const arrival = arrivals.current.find(item => item.key === key); if (arrival) arrival.ready = true; pump(); }, arrivalAt - now);
  }, [dispatch, flush, later, pump, refreshLayout]);
  return { click, flush, cancel: flush, flights, queue, hiddenSlotIds, hiddenTransitionIds, eliminatingIds,
    hasPending: hasPending || flights.length > 0, refreshLayout,
    getState: () => shadow.current };
};
